import React from "react";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import {
  FooterContainer,
  FooterSkipButton,
  OptionsSection,
  StepContent,
  StepTitle,
  WizardContentWrapper,
  FooterButton,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { MDSIcon, MDSIconKind, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import {
  AppDownloadButton,
  AppIconWrapper,
} from "@/components/onboarding/components/app-download-button";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { DownloadDesktopAppStep } from "@/components/onboarding/components/welcome-wizard/steps/post-import/download-desktop-app-step";
import { openWindowUrlInNewTab } from "@/modules/window/navigation";
import { APP_CONFIG } from "@/domains/global/config";
import { useAppStore } from "@/store";

export const DownloadAppsStep: React.FC<StepRenderProps> = ({ onNavToStep }) => {
  const { store } = useAppStore();
  const isChromeExtensionInstalled = !!store.chromeExtension.isInstalled.data;

  const handleInstallExtension = async () => {
    trackEvent(TrackedEvent.ImportWizardStepChange, {
      from: "download-apps",
      to: "external_link",
      source: "welcome_wizard",
    });
    trackEvent(TrackedEvent.OnboardingDownloadClick, {
      option: "chrome-extension",
    });
    trackEvent(TrackedEvent.ChromeExtensionOnboardingInstallerClick);
    await openWindowUrlInNewTab({ url: new URL(APP_CONFIG.URLS.CHROME_EXTENSION) });
  };

  const handleDownloadiOSApp = async () => {
    trackEvent(TrackedEvent.ImportWizardStepChange, {
      from: "download-apps",
      to: "download-ios-app",
      source: "welcome_wizard",
    });
    trackEvent(TrackedEvent.OnboardingDownloadClick, {
      option: "ios-app",
    });
    await openWindowUrlInNewTab({ url: new URL(APP_CONFIG.URLS.IOS_APP_V2) });
  };

  const handleNext = () => {
    onNavToStep?.("intro-video");
  };

  useEffectOnMount(() => {
    trackEvent(TrackedEvent.ChromeExtensionOnboardingInstallerView);
  });

  return (
    <WizardContentWrapper>
      <StepTitle>More ways to capture your thoughts</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Record meetings, capture thoughts on-the-go, and save web pages. Mem turns everything into
          organized, searchable notes.
        </MDSText>

        <OptionsSection>
          <DownloadDesktopAppStep />
          <AppDownloadButton onClick={handleDownloadiOSApp}>
            <AppIconWrapper data-icon-wrapper isApple={true}>
              <MDSIcon kind={MDSIconKind.Mobile} size={16} />
            </AppIconWrapper>
            <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
              Download iOS app
            </MDSText>
          </AppDownloadButton>
          {!isChromeExtensionInstalled && (
            <AppDownloadButton onClick={handleInstallExtension}>
              <AppIconWrapper data-icon-wrapper isApple={false}>
                <MDSIcon kind={MDSIconKind.GoogleChrome} size={16} />
              </AppIconWrapper>
              <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                Install Chrome extension
              </MDSText>
            </AppDownloadButton>
          )}
        </OptionsSection>
      </StepContent>

      <FooterContainer align="space-between">
        <FooterSkipButton onClick={handleNext}>Skip for now</FooterSkipButton>
        <FooterButton onClick={handleNext}>Next</FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};
